// src/components/shell/popovers/ActivityBannerBell.tsx
// Visual + interaction surface for the TopStrip bell that opens
// ActivityBannerPopover. Preserves the legacy pulse + unread badge so this
// button stays a drop-in replacement for the old WorkplaceMessagesControl bell.
import { forwardRef } from "react";

export type ActivityBannerBellProps = {
  open: boolean;
  unreadCount: number;
  unackedCount: number;
  reduceMotion: boolean;
  onClick: () => void;
};

export const ActivityBannerBell = forwardRef<HTMLButtonElement, ActivityBannerBellProps>(
  function ActivityBannerBell({ open, unreadCount, unackedCount, reduceMotion, onClick }, ref) {
    const live = unreadCount > 0 || unackedCount > 0;
    const badgeCount = unreadCount > 0 ? unreadCount : unackedCount;
    const pulseClass = live && !reduceMotion ? "animate-pulse" : "";
    const ariaParts = [
      unreadCount > 0 ? `${unreadCount} unread` : null,
      unackedCount > 0 ? `${unackedCount} need acknowledgement` : null,
    ].filter(Boolean);
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={
          "relative rounded-full p-2.5 border border-transparent text-gray-600 dark:text-gray-300 " +
          "hover:bg-gray-100/90 dark:hover:bg-zinc-800/90 focus:outline-none focus-visible:ring-2 " +
          "focus-visible:ring-blue-500/70 dark:focus-visible:ring-slate-400/80 " +
          (live
            ? `text-blue-600 dark:text-slate-100 shadow-[0_0_14px_rgba(59,130,246,0.55)] ` +
              `dark:shadow-[0_0_18px_rgba(226,232,240,0.42)] ring-2 ring-blue-400/55 dark:ring-slate-300/45 ${pulseClass}`
            : "")
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={live ? `Activity, ${ariaParts.join(", ")}` : "Activity"}
      >
        <span className="sr-only">Activity</span>
        <svg
          className="h-6 w-6 sm:h-7 sm:w-7"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {live ? (
          <span
            className={
              "absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full text-[10px] font-bold leading-[1.125rem] text-center " +
              (unreadCount > 0
                ? "bg-blue-600 dark:bg-slate-200 text-white dark:text-zinc-900"
                : "bg-amber-500 text-white")
            }
          >
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        ) : null}
      </button>
    );
  }
);
